// Hover probe for the UC segmentation raster overlay (spec 2026-06-19).
//
// The coloured overlay tiles can't be reverse-mapped to a class (they're alpha-blended over the
// slide). The source of truth is the *label* tile — a 256x256 image where each pixel's value is the
// class index. This probe lazily fetches+decodes the label tile under the cursor, caches the decoded
// index array, and answers `probe(x, y, z)` in O(1) — so hovering has no per-move network cost and is
// instant after a tile's first visit.
//
// Separation of concerns: this class owns only the cache + the tile/pixel coordinate maths. Auth,
// fetching and PNG decoding are injected via `loadTile` (the viewer provides them), keeping this
// framework-agnostic and unit-testable.

const TILE = 256;

export default class SegmentationLabelProbe {
  /**
   * @param {Object} o
   * @param {number}   o.width        slide width in px
   * @param {number}   o.height       slide height in px
   * @param {number[]} o.resolutions  world-units/pixel per level, index = z (e.g. [512,…,1])
   * @param {(z:number,x:number,y:number)=>Promise<Uint8Array|null>} o.loadTile
   *        resolve a 256*256 class-index byte array for tile (z,x,y), or null when empty/absent
   * @param {()=>void} [o.onUpdate]   called when a lazily-loaded tile arrives (re-probe to refresh)
   * @param {number}   [o.tileSize]   defaults to 256
   */
  constructor({width, height, resolutions, loadTile, onUpdate, tileSize = TILE}) {
    this.width = width;
    this.height = height;
    this.resolutions = resolutions || [];
    this.loadTile = loadTile;
    this.onUpdate = onUpdate || (() => {});
    this.tileSize = tileSize;
    this._cache = new Map(); // "z/x/y" -> Uint8Array | null (loaded-empty) | 'loading'
  }

  /**
   * Class index at world coordinate (x, y) for pyramid level z. Uses the same grid as the build
   * (origin [0,H], tile (z,x,y) world bbox x0=x·256·r, maxY=H−y·256·r). Lazily loads the tile on a
   * cache miss and returns 'pending' (re-probe via onUpdate when it arrives).
   * @returns {{status:'ready', index:number}|{status:'empty'}|{status:'pending'}|{status:'out'}}
   */
  probe(x, y, z) {
    if (z == null || z < 0 || z >= this.resolutions.length) {
      return {status: 'out'};
    }
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) {
      return {status: 'out'};
    }
    const r = this.resolutions[z];
    const span = this.tileSize * r;
    const tx = Math.floor(x / span);
    const ty = Math.floor((this.height - y) / span);
    const key = z + '/' + tx + '/' + ty;

    const cached = this._cache.get(key);
    if (cached === undefined) {
      this._load(key, z, tx, ty);
      return {status: 'pending'};
    }
    if (cached === 'loading') {
      return {status: 'pending'};
    }
    if (cached === null) {
      return {status: 'empty'};
    }

    let px = Math.floor(x / r) - tx * this.tileSize;
    let py = Math.floor((this.height - y) / r) - ty * this.tileSize;
    px = px < 0 ? 0 : (px >= this.tileSize ? this.tileSize - 1 : px);
    py = py < 0 ? 0 : (py >= this.tileSize ? this.tileSize - 1 : py);
    const index = cached[py * this.tileSize + px];
    return index > 0 ? {status: 'ready', index} : {status: 'empty'};
  }

  _load(key, z, tx, ty) {
    this._cache.set(key, 'loading');
    Promise.resolve(this.loadTile(z, tx, ty))
      .then(arr => {
        this._cache.set(key, arr || null);
        this.onUpdate();
      })
      .catch(() => {
        this._cache.set(key, null);
      });
  }

  clear() {
    this._cache.clear();
  }
}
